/**
 * @fileoverview Certificate Viewer - Detailed view with 5-layer verification
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from '@/hooks/useTranslation';
import { useDateFormat } from '@/hooks/useDateFormat';
import { walletService } from '@/services/walletService';
import {
    ArrowLeft,
    Shield,
    CheckCircle,
    XCircle,
    Download,
    ExternalLink,
    Calendar,
    User,
    Hash,
    Link as LinkIcon,
    FileText,
    AlertCircle,
    Database,
    Globe,
    Lock,
    ShieldCheck
} from 'lucide-react';

export const CertificateViewer = () => {
    const { certId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { formatDate } = useDateFormat();
    const [certificate, setCertificate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('certificate');
    const [downloading, setDownloading] = useState(null); // Track which file is downloading

    useEffect(() => {
        loadCertificate();
    }, [certId]);

    const loadCertificate = async () => {
        try {
            setLoading(true);
            const data = await walletService.getCertificateDetails(certId);
            setCertificate(data);
        } catch (err) {
            console.error('Error loading certificate:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (target) => {
        try {
            setDownloading(target);
            let blob;
            let filename;

            if (target === 'certificate') {
                blob = await walletService.downloadCertificate(certId);
                filename = `certificado-${certificate?.manifest?.certificateNumber || certId}.svg`;
            } else {
                blob = await walletService.downloadAttachment(certId, target);
                filename = target;
            }

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Download failed:', error);
            alert(t('wallet.downloadFailed', 'Error al descargar el archivo'));
        } finally {
            setDownloading(null);
        }
    };


    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-[600px]">
                <div className="flex flex-col items-center space-y-4">
                    <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    <p className="text-slate-600">{t('wallet.loadingCertificate', 'Cargando certificado...')}</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-2xl mx-auto">
                <div className="bg-red-50 border border-red-200 rounded-lg p-8">
                    <div className="flex items-center space-x-3 text-red-800 mb-4">
                        <XCircle className="w-8 h-8" />
                        <div>
                            <h3 className="font-semibold text-lg">{t('common.error', 'Error')}</h3>
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    </div>
                    <button
                        onClick={() => navigate('/wallet')}
                        className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                        {t('wallet.backToList', 'Volver a Mis Certificados')}
                    </button>
                </div>
            </div>
        );
    }

    const { manifest, verification, attachments } = certificate || {};
    const explorerUrl = 'https://polygonscan.com/tx/';
    const ipfsGateway = 'https://gateway.pinata.cloud/ipfs/';

    // Determine status badge
    const StatusBadge = () => {
        if (manifest?.status === 'revoked') {
            return (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                    <XCircle className="w-4 h-4 mr-1" />
                    {t('wallet.status.revoked', 'Revocado')}
                </span>
            );
        }
        if (manifest?.status === 'verified_on_chain' || manifest?.status === 'blockchain_registered' || manifest?.status === 'finalized') {
            return (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    <ShieldCheck className="w-4 h-4 mr-1" />
                    {t('wallet.status.verified', 'Verificado en Blockchain')}
                </span>
            );
        }
        if (manifest?.status === 'ipfs_uploaded') {
            return (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                    <Globe className="w-4 h-4 mr-1" />
                    {t('wallet.status.ipfs', 'Almacenado en IPFS')}
                </span>
            );
        }
        if (manifest?.status === 'completed' || manifest?.status === 'svg_generated') {
            return (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    {t('wallet.status.completed', 'Completado')}
                </span>
            );
        }
        return (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                <AlertCircle className="w-4 h-4 mr-1" />
                {t('wallet.status.processing', 'En Proceso')}
            </span>
        );
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <button
                    onClick={() => navigate('/wallet')}
                    className="flex items-center space-x-2 text-slate-600 hover:text-slate-900 transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                    <span className="font-medium">{t('wallet.backToList', 'Volver a Mis Certificados')}</span>
                </button>

                <button
                    onClick={() => handleDownload('certificate')}
                    disabled={downloading === 'certificate'}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {downloading === 'certificate' ? (
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                        <Download className="w-4 h-4" />
                    )}
                    <span>
                        {downloading === 'certificate'
                            ? t('wallet.downloading', 'Descargando...')
                            : t('wallet.download', 'Descargar')}
                    </span>
                </button>
            </div>

            {/* Certificate Info Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6">
                    <div className="flex items-start justify-between">
                        <div className="space-y-2">
                            <div className="flex items-center space-x-3">
                                <Shield className="w-8 h-8 text-white" />
                                <h1 className="text-2xl font-bold text-white">
                                    {manifest?.certificateNumber || certId}
                                </h1>
                            </div>
                            <StatusBadge />
                        </div>
                    </div>
                </div>

                {/* Metadata Grid */}
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Recipient */}
                    <div className="space-y-1">
                        <div className="flex items-center space-x-2 text-sm text-slate-500">
                            <User className="w-4 h-4" />
                            <span>{t('wallet.recipient', 'Destinatario')}</span>
                        </div>
                        <p className="font-medium text-slate-900 break-all leading-tight">
                            {manifest?.recipientName ||
                                verification?.blockchain?.addresses?.recipient ||
                                manifest?.recipientWallet ||
                                t('wallet.unknown', 'Desconocido')}
                        </p>
                        {manifest?.recipientEmail && (
                            <p className="text-sm text-slate-600">{manifest.recipientEmail}</p>
                        )}
                    </div>

                    {/* Issuer */}
                    <div className="space-y-1">
                        <div className="flex items-center space-x-2 text-sm text-slate-500">
                            <User className="w-4 h-4" />
                            <span>{t('wallet.issuedBy', 'Emitido por')}</span>
                        </div>
                        <p className="font-medium text-slate-900">
                            {manifest?.issuer?.name || 'Unknown'}
                        </p>
                        {manifest?.tenant?.name && (
                            <p className="text-sm text-slate-600">{manifest.tenant.name}</p>
                        )}
                    </div>

                    {/* Date */}
                    <div className="space-y-1">
                        <div className="flex items-center space-x-2 text-sm text-slate-500">
                            <Calendar className="w-4 h-4" />
                            <span>{t('wallet.issuedAt', 'Fecha de emisión')}</span>
                        </div>
                        <p className="font-medium text-slate-900">
                            {formatDate(manifest?.issuedAt, 'full')}
                        </p>
                    </div>

                    {/* Document ID */}
                    <div className="space-y-1">
                        <div className="flex items-center space-x-2 text-sm text-slate-500">
                            <Hash className="w-4 h-4" />
                            <span>{t('wallet.documentId', 'ID Documento')}</span>
                        </div>
                        <p className="font-mono text-sm text-slate-900 font-semibold">
                            {manifest?.certificateNumber}
                        </p>
                    </div>

                    {/* IPFS Hash */}
                    {verification?.ipfsHash?.expected && (
                        <div className="space-y-1 md:col-span-2">
                            <div className="flex items-center space-x-2 text-sm text-slate-500">
                                <Globe className="w-4 h-4" />
                                <span>{t('wallet.ipfsCid', 'IPFS CID')}</span>
                            </div>
                            <a
                                href={`${ipfsGateway}${verification.ipfsHash.expected}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-mono text-xs text-blue-600 hover:text-blue-800 hover:underline break-all flex items-center gap-1"
                            >
                                {verification.ipfsHash.expected}
                                <ExternalLink className="w-3 h-3 flex-shrink-0" />
                            </a>
                        </div>
                    )}

                    {/* Blockchain TX Hash */}
                    {verification?.blockchain?.txHash && (
                        <div className="space-y-1 md:col-span-2 lg:col-span-3">
                            <div className="flex items-center space-x-2 text-sm text-slate-500">
                                <LinkIcon className="w-4 h-4" />
                                <span>{t('wallet.txHash', 'Blockchain TX Hash')}</span>
                            </div>
                            <a
                                href={`${explorerUrl}${verification.blockchain.txHash}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-mono text-xs text-blue-600 hover:text-blue-800 hover:underline break-all flex items-center gap-1"
                            >
                                {verification.blockchain.txHash}
                                <ExternalLink className="w-3 h-3 flex-shrink-0" />
                            </a>
                        </div>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-slate-200">
                <nav className="flex space-x-8">
                    <button
                        onClick={() => setActiveTab('certificate')}
                        className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'certificate'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-slate-500 hover:text-slate-700'
                            }`}
                    >
                        <div className="flex items-center space-x-2">
                            <FileText className="w-4 h-4" />
                            <span>{t('wallet.tabs.certificate', 'Certificado')}</span>
                        </div>
                    </button>

                    <button
                        onClick={() => setActiveTab('verification')}
                        className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'verification'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-slate-500 hover:text-slate-700'
                            }`}
                    >
                        <div className="flex items-center space-x-2">
                            <Shield className="w-4 h-4" />
                            <span>{t('wallet.tabs.verification', 'Verificación')}</span>
                        </div>
                    </button>

                    {attachments && attachments.length > 0 && (
                        <button
                            onClick={() => setActiveTab('attachments')}
                            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'attachments'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-slate-500 hover:text-slate-700'
                                }`}
                        >
                            <div className="flex items-center space-x-2">
                                <Download className="w-4 h-4" />
                                <span>{t('wallet.tabs.attachments', `Adjuntos (${attachments.length})`)}</span>
                            </div>
                        </button>
                    )}
                </nav>
            </div>

            {/* Tab Content */}
            <div>
                {activeTab === 'certificate' && (
                    <div className="bg-white rounded-lg border border-slate-200 p-8">
                        {certificate?.certificate?.content ? (
                            <div
                                className="w-full"
                                dangerouslySetInnerHTML={{
                                    __html: new TextDecoder('utf-8').decode(Uint8Array.from(atob(certificate.certificate.content), c => c.charCodeAt(0)))
                                }}
                            />
                        ) : (
                            <div className="text-center py-16">
                                <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-slate-700 mb-2">
                                    {t('wallet.certificateNotDecrypted', 'Contenido No Disponible')}
                                </h3>
                                <p className="text-slate-600">
                                    {t('wallet.decryptionPending', 'El archivo SVG del certificado no se encontró en el servidor.')}
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'verification' && (
                    <div className="space-y-4">
                        <div className="bg-white rounded-xl border border-slate-200 p-6">
                            <h3 className="font-bold text-lg text-slate-900 mb-6 flex items-center gap-2">
                                <ShieldCheck className="w-6 h-6 text-blue-600" />
                                {t('wallet.verificationTitle', 'Cadena de Verificación')}
                            </h3>

                            <div className="space-y-5">
                                {/* Layer 1: DB Existence */}
                                <VerificationLayer
                                    icon={<Database className="w-5 h-5" />}
                                    title={t('wallet.verify.existence', 'Registro en Base de Datos')}
                                    description={t('wallet.verify.existenceDesc', 'El certificado existe en el sistema del emisor')}
                                    verified={true}
                                />

                                {/* Layer 2: IPFS */}
                                <VerificationLayer
                                    icon={<Globe className="w-5 h-5" />}
                                    title={t('wallet.verify.ipfs', 'Almacenado en IPFS')}
                                    description={
                                        verification?.ipfsHash?.available
                                            ? t('wallet.verify.ipfsOk', 'Documento cifrado almacenado en red distribuida (inmutable)')
                                            : t('wallet.verify.ipfsPending', 'Pendiente de subida a IPFS')
                                    }
                                    verified={verification?.ipfsHash?.available}
                                    link={verification?.ipfsHash?.expected ? `${ipfsGateway}${verification.ipfsHash.expected}` : null}
                                    linkLabel={t('wallet.verify.viewIpfs', 'Ver en IPFS')}
                                    detail={verification?.ipfsHash?.expected}
                                />

                                {/* Layer 3: Blockchain */}
                                <VerificationLayer
                                    icon={<LinkIcon className="w-5 h-5" />}
                                    title={t('wallet.verify.blockchain', 'Registrado en Blockchain (Polygon)')}
                                    description={
                                        verification?.blockchain?.verified
                                            ? t('wallet.verify.blockchainOk', 'Hash registrado on-chain — inmutable y verificable públicamente')
                                            : t('wallet.verify.blockchainPending', 'Pendiente de registro en blockchain')
                                    }
                                    verified={verification?.blockchain?.verified}
                                    link={verification?.blockchain?.txHash ? `${explorerUrl}${verification.blockchain.txHash}` : null}
                                    linkLabel={t('wallet.verify.viewPolygonscan', 'Ver en Polygonscan')}
                                    detail={verification?.blockchain?.txHash}
                                >
                                    {/* On-Chain Wallets (Inside Layer 3) */}
                                    {verification?.blockchain?.addresses && (
                                        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 bg-white/50 p-3 rounded-lg border border-green-100">
                                            <div className="min-w-0">
                                                <p className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">{t('wallet.onChain.tenant', 'Org Wallet')}</p>
                                                <p className="font-mono text-[9px] text-slate-600 truncate" title={verification.blockchain.addresses.tenant}>{verification.blockchain.addresses.tenant}</p>
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">{t('wallet.onChain.issuer', 'Issuer Wallet')}</p>
                                                <p className="font-mono text-[9px] text-slate-600 truncate" title={verification.blockchain.addresses.issuer}>{verification.blockchain.addresses.issuer}</p>
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">{t('wallet.onChain.recipient', 'Wallet Destinatario')}</p>
                                                <p className="font-mono text-[9px] text-slate-600 truncate" title={verification.blockchain.addresses.recipient}>{verification.blockchain.addresses.recipient}</p>
                                            </div>
                                        </div>
                                    )}
                                </VerificationLayer>

                                {/* Layer 4: Integrity */}
                                <VerificationLayer
                                    icon={<Lock className="w-5 h-5" />}
                                    title={t('wallet.verify.integrity', 'Integridad Verificada')}
                                    description={
                                        verification?.blockchain?.verified && verification?.ipfsHash?.available
                                            ? t('wallet.verify.integrityOk', 'El hash de IPFS coincide con el registrado en Blockchain. El contenido del documento es íntegro y original.')
                                            : t('wallet.verify.integrityPending', 'Requiere registro en blockchain + IPFS para verificar integridad')
                                    }
                                    verified={verification?.blockchain?.verified && verification?.ipfsHash?.available}
                                >
                                    {/* Manifest Fields (Inside Layer 4) */}
                                    {manifest?.fieldValues && Object.keys(manifest.fieldValues).length > 0 && (
                                        <div className="mt-4 p-4 bg-slate-50/80 rounded-xl border border-blue-100/50">
                                            <div className="flex items-center gap-2 mb-3 border-b border-blue-100 pb-2">
                                                <FileText className="w-4 h-4 text-indigo-500" />
                                                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                                                    {t('wallet.metadataFromManifest', 'Metadata del Documento (Manifest)')}
                                                </span>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                                                {Object.entries(manifest.fieldValues).map(([key, value]) => (
                                                    <div key={key} className="min-w-0">
                                                        <p className="text-[8px] uppercase text-slate-400 font-bold truncate leading-tight mb-0.5">{key.replace(/_/g, ' ')}</p>
                                                        <p className="text-xs text-slate-800 font-semibold break-words leading-tight" title={value?.toString()}>
                                                            {value?.toString() || '-'}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Raw JSON Toggle */}
                                            {manifest?.raw && (
                                                <div className="mt-4 pt-4 border-t border-blue-100">
                                                    <details className="group">
                                                        <summary className="text-[9px] font-bold uppercase tracking-widest text-blue-500 cursor-pointer hover:text-blue-700 flex items-center gap-1 list-none">
                                                            <span className="group-open:rotate-90 transition-transform">▶</span>
                                                            {t('wallet.viewRawManifest', 'Ver manifest.json completo (JSON)')}
                                                        </summary>
                                                        <pre className="mt-2 p-3 bg-slate-900 rounded-lg text-[10px] text-green-400 overflow-x-auto font-mono leading-relaxed max-h-48 overflow-y-auto shadow-inner">
                                                            {JSON.stringify(manifest.raw, null, 2)}
                                                        </pre>
                                                    </details>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </VerificationLayer>

                                {/* Layer 5: Not Revoked */}
                                <VerificationLayer
                                    icon={<ShieldCheck className="w-5 h-5" />}
                                    title={t('wallet.verify.notRevoked', 'No Revocado')}
                                    description={
                                        manifest?.status !== 'revoked'
                                            ? t('wallet.verify.notRevokedOk', 'El certificado está activo y no ha sido revocado')
                                            : t('wallet.verify.revoked', 'Este certificado ha sido revocado por el emisor')
                                    }
                                    verified={manifest?.status !== 'revoked'}
                                />
                            </div>

                            {/* Blockchain Timestamp */}
                            {verification?.blockchain?.mintedAt && (
                                <div className="mt-6 pt-3 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400 italic">
                                    <span>{t('wallet.verify.registeredAt', 'Fecha oficial de registro')}: {formatDate(verification.blockchain.mintedAt, 'full')}</span>
                                    <span>v2.0.0-auditable</span>
                                </div>
                            )}
                        </div>
                    </div>
                )
                }

                {
                    activeTab === 'attachments' && (
                        <div className="bg-white rounded-lg border border-slate-200 p-6">
                            <h3 className="font-semibold text-slate-900 mb-4">
                                {t('wallet.attachments', 'Adjuntos')}
                            </h3>

                            {attachments && attachments.length > 0 ? (
                                <div className="space-y-2">
                                    {attachments.map((attachment, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                                        >
                                            <div className="flex items-center space-x-3">
                                                <FileText className="w-5 h-5 text-slate-600" />
                                                <div>
                                                    <p className="font-medium text-slate-900">{attachment.name}</p>
                                                    <p className="text-sm text-slate-500">
                                                        {(attachment.size / 1024).toFixed(1)} KB
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleDownload(attachment.name)}
                                                disabled={downloading === attachment.name}
                                                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px] justify-center"
                                            >
                                                {downloading === attachment.name ? (
                                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                ) : (
                                                    <Download className="w-4 h-4" />
                                                )}
                                                <span>
                                                    {downloading === attachment.name
                                                        ? t('wallet.downloading', 'Descargando...')
                                                        : t('wallet.download', 'Descargar')}
                                                </span>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                                    <p className="text-slate-600">
                                        {t('wallet.noAttachments', 'Sin adjuntos disponibles')}
                                    </p>
                                </div>
                            )}
                        </div>
                    )
                }
            </div >
        </div >
    );
};

/**
 * Verification Layer Component — reusable row for each verification check
 */
const VerificationLayer = ({ icon, title, description, verified, link, linkLabel, detail, children }) => (
    <div className={`flex items-start p-4 rounded-lg border ${verified
        ? 'border-green-200 bg-green-50/50'
        : 'border-slate-200 bg-slate-50/50'
        }`}>
        <div className={`flex-shrink-0 mt-0.5 ${verified ? 'text-green-600' : 'text-slate-400'}`}>
            {verified ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
        </div>
        <div className="ml-3 flex-1 min-w-0">
            <div className="flex items-center gap-2">
                <span className={`${verified ? 'text-green-700' : 'text-slate-500'}`}>{icon}</span>
                <p className={`font-semibold ${verified ? 'text-green-900' : 'text-slate-700'}`}>
                    {title}
                </p>
            </div>
            <p className={`text-sm mt-0.5 ${verified ? 'text-green-700' : 'text-slate-500'}`}>
                {description}
            </p>
            {detail && (
                <p className="font-mono text-xs text-slate-500 mt-1 break-all">
                    {detail}
                </p>
            )}

            {/* Children content (expanded details) */}
            {children && (
                <div className="mt-2">
                    {children}
                </div>
            )}

            {link && (
                <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline mt-1"
                >
                    {linkLabel || 'Ver'}
                    <ExternalLink className="w-3 h-3" />
                </a>
            )}
        </div>
    </div>
);
